// Full-screen mobile search modal — same flow Airbnb's mobile app
// uses behind its "Start your search" pill. Two sections:
//
//   "Unde?"        — free-text input + 6 city shortcuts + an
//                    "Aproape de tine" geolocation shortcut
//   "Serviciu"     — collapsible with the full ServiceCategory list
//
// No "Când" panel: Petzy's booking model picks the salon first and
// then surfaces that salon's own availability calendar on its
// detail page. A pre-search date filter would lie about what the
// platform can actually answer, so the surface is dropped from the
// modal entirely.
//
// Writes back to ?city= / ?category= / ?q= on submit so the page's
// existing fetch effect picks the changes up automatically.
//
// Rendered by HomePage (the merged marketplace surface). Visible only
// when `open === true`.

import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  FiX,
  FiSearch,
  FiCompass,
  FiChevronDown,
  FiChevronUp,
  FiScissors,
  FiMapPin,
} from 'react-icons/fi';
import api from '../../utils/api';
import { mapApiSalon } from '../../utils/salonMapper';
import useKeyboardInset from '../../hooks/useKeyboardInset';
import CategoryGrid from './CategoryGrid';
import {
  CATEGORIES,
  getActiveCategoryId,
  commitCategoryToUrl,
  clearFilterParams,
} from './categories';
import { geolocateNearestCity } from '../../utils/snapNearestCity';


export default function WhereModal({ open, onClose }) {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  // Lift the bottom bar in sync with the keyboard (no native-resize lag). We
  // own the input focus (no autoFocus) so the very first keyboard show is
  // already tracked.
  useKeyboardInset(rootRef, open, inputRef);
  const [searchParams, setSearchParams] = useSearchParams();
  const [where, setWhere] = useState(searchParams.get('q') || searchParams.get('city') || '');
  // Live salon suggestions for the typed query (name / city / neighbourhood).
  const [suggestions, setSuggestions] = useState([]);
  // Draft category id, lifted from the URL when the modal opens.
  // Same id space as the page strip ("all" / "full-grooming" / "cat-grooming" / …).
  const [draftCategoryId, setDraftCategoryId] = useState('all');
  const [serviceOpen, setServiceOpen] = useState(false);
  const [geoState, setGeoState] = useState('idle'); // idle | loading | denied | error
  const [prevOpen, setPrevOpen] = useState(false);

  // Sync the draft form to the URL when the modal opens (the closed-to-open
  // edge), derived during render instead of in an effect. The parent passes a
  // fresh inline `onClose` each render, so an effect keyed on it re-runs on
  // unrelated parent re-renders; a sync living inside that effect would wipe
  // whatever the user has typed or geolocated mid-session. The open edge
  // resets exactly once, when the modal actually opens.
  if (open && !prevOpen) {
    setPrevOpen(true);
    setWhere(searchParams.get('q') || searchParams.get('city') || '');
    setDraftCategoryId(getActiveCategoryId(searchParams));
    setServiceOpen(false);
    setGeoState('idle');
  } else if (!open && prevOpen) {
    setPrevOpen(false);
  }

  useEffect(() => {
    if (!open) return;
    // Lock background scroll while the modal is up.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // Debounced live suggestions. The backend ?query= ORs name/description/city,
  // so typing matches salons by name OR location. Hooks stay above the early
  // return so order is stable.
  useEffect(() => {
    const q = where.trim();
    if (!open || q.length < 2) { setSuggestions([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get('/salons', { params: { query: q, limit: 6 } })
        .then(({ data }) => { if (!cancelled) setSuggestions((data?.salons || []).map(mapApiSalon)); })
        .catch(() => { if (!cancelled) setSuggestions([]); });
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [where, open]);

  if (!open) return null;

  const requestGeolocation = async () => {
    // Guard against rapid re-clicks while a fix is still in flight.
    // Without it, every tap fires another geolocateNearestCity, and
    // the resolve order races onto the same setWhere / setGeoState
    // setters — sometimes the second tap's "loading" stomps the first
    // tap's success.
    if (geoState === 'loading') return;
    setGeoState('loading');
    const { city, errorCode } = await geolocateNearestCity();
    if (city) {
      setWhere(city);
      setGeoState('idle');
      return;
    }
    setGeoState(errorCode === 'denied' ? 'denied' : 'error');
  };

  const apply = () => {
    setSearchParams((prev) => {
      // Apply the draft category through the shared helper first — it returns
      // a fresh clone (so React Router's memoized `prev` is never mutated) and
      // keeps the ?category= vs ?species= split (e.g. Pisici uses ?species=CAT)
      // consistent with the page strip and FilterModal. q + city below are
      // disjoint params, so layering them on the clone afterwards is order-safe.
      const entry = CATEGORIES.find((c) => c.id === draftCategoryId);
      const next = commitCategoryToUrl(prev, entry?.id === 'all' ? null : entry);
      // The field is a GENERAL search (salon name OR location), so it owns ?q=
      // — the backend ORs name + description + city, so "Bubble" finds Bubble
      // Pet and "cluj" finds Cluj-Napoca salons. Drop any stale ?city= so the
      // two don't AND-narrow each other.
      const trimmed = where.trim();
      if (trimmed) {
        next.set('q', trimmed);
      } else {
        next.delete('q');
      }
      next.delete('city');
      return next;
    });
    onClose();
  };

  const clearAll = () => {
    setWhere('');
    setDraftCategoryId('all');
    setSearchParams((prev) => clearFilterParams(prev));
  };

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label="Unde cauți"
      className="fixed inset-0 z-[90] flex flex-col bg-cream-100"
      style={{ transition: 'padding-bottom 0.25s ease-out' }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-ink/10 bg-cream-50 px-4 pb-3 pt-[max(env(safe-area-inset-top,0px),0.75rem)]">
        <button
          type="button"
          onClick={onClose}
          aria-label="Închide"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/15 text-ink-600 transition-colors hover:bg-ink/5"
        >
          <FiX className="h-4 w-4" />
        </button>
        <span className="font-display text-sm font-semibold text-ink">Caută saloane</span>
        <span className="h-9 w-9" aria-hidden="true" />
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 pb-32 pt-4">
        {/* Where card */}
        <section className="rounded-2xl bg-cream-50 p-5 shadow-sm">
          <h2 className="font-display text-2xl font-bold text-ink">Ce cauți?</h2>
          <label className="mt-4 flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2.5 focus-within:border-ink">
            <FiSearch className="h-4 w-4 text-ink-400" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              value={where}
              onChange={(e) => setWhere(e.target.value)}
              aria-label="Caută salon, oraș sau cartier"
              placeholder="Caută salon, oraș sau cartier..."
              className="w-full border-none bg-transparent p-0 text-sm text-ink placeholder-ink-400 outline-none focus:ring-0"
            />
          </label>

          {/* Live salon suggestions (name / city) as you type — tap to open. */}
          {suggestions.length > 0 && (
            <div className="mt-3 overflow-hidden rounded-xl border border-ink/10">
              {suggestions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => { navigate(`/salon/${s.slug}`); onClose(); }}
                  className="flex w-full items-center gap-3 border-b border-ink/5 px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-ink/5"
                >
                  {s.image ? (
                    <img src={s.image} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                  ) : (
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-pastel-peach">
                      <FiMapPin className="h-4 w-4 text-pastel-peach-deep" />
                    </span>
                  )}
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-ink">{s.name}</span>
                    <span className="block truncate text-xs text-ink-500">
                      {s.city}{s.address ? `, ${s.address}` : ''}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}

          <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-ink-500">
            Acces rapid
          </p>

          {/* Aproape de tine — geolocation */}
          <button
            type="button"
            onClick={requestGeolocation}
            className="mt-3 flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-ink/5"
          >
            <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-pastel-sky">
              <FiCompass className="h-5 w-5 text-pastel-sky-deep" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-ink">Aproape de tine</span>
              <span className="block truncate text-xs text-ink-500">
                {geoState === 'loading' && 'Caut locația...'}
                {geoState === 'denied' && 'Permisiune de locație refuzată'}
                {geoState === 'error' && 'Nu am putut detecta locația'}
                {geoState === 'idle' && 'Folosește locația ta curentă'}
              </span>
            </span>
          </button>

          <p className="mt-4 text-[11px] text-ink-500">
            Scrie numele salonului, orașul sau cartierul — căutarea găsește după orice.
          </p>
        </section>

        {/* Serviciu — same 13-chip grid as the page strip + FilterModal */}
        <CollapsibleCard
          title="Serviciu"
          subtitle={
            serviceOpen
              ? ''
              : (CATEGORIES.find((c) => c.id === draftCategoryId && c.id !== 'all')?.label
                 || 'Selectează ce dorește blănosul')
          }
          Icon={FiScissors}
          open={serviceOpen}
          onToggle={() => setServiceOpen((v) => !v)}
        >
          <CategoryGrid value={draftCategoryId} onChange={(entry) => setDraftCategoryId(entry.id)} />
        </CollapsibleCard>
      </div>

      {/* Sticky footer */}
      <div className="border-t border-ink/10 bg-cream-50 px-4 pt-3 pb-[max(env(safe-area-inset-bottom,0px),0.75rem)]">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={clearAll}
            className="text-sm font-semibold text-ink-600 underline-offset-4 hover:underline"
          >
            Șterge tot
          </button>
          <button
            type="button"
            onClick={apply}
            className="flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white shadow-md transition-colors hover:bg-primary-600"
          >
            <FiSearch className="h-4 w-4" />
            Caută
          </button>
        </div>
      </div>
    </div>
  );
}

// Reusable card with collapse animation. Header is always visible
// and toggles `open`; the body slides in via display swap.
function CollapsibleCard({ title, subtitle, Icon, open, onToggle, children }) {
  return (
    <section className="mt-4 overflow-hidden rounded-2xl bg-cream-50 shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-5 py-4 text-left"
      >
        <span className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink/5">
            <Icon className="h-4 w-4 text-ink-600" />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-ink">{title}</span>
            {subtitle && <span className="block truncate text-xs text-ink-500">{subtitle}</span>}
          </span>
        </span>
        {open
          ? <FiChevronUp className="h-4 w-4 text-ink-400" aria-hidden="true" />
          : <FiChevronDown className="h-4 w-4 text-ink-400" aria-hidden="true" />
        }
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </section>
  );
}
